import React, { createContext, useContext, useState, useEffect, useRef, use } from 'react';
import { Constants } from '@videosdk.live/react-sdk';
import * as meetingServices from '../services/meetingServices';
import { useAutoLogout } from "../hooks/useAutoLogout";
import { useForceAutoLogout } from "../hooks/useForceAutoLogout";

// Create context
const SiteContext = createContext();

// Custom hook to use the context
export const useSiteContext = () => {
    const context = useContext(SiteContext);
    if (!context) {
        throw new Error('useSiteContext must be used within a SiteProvider');
    }
    return context;
};

// Provider component
export const SiteProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [meetingRole, setMeetingRole] = useState(null);
    const [isSafari, setIsSafari] = useState(false);
    const [meetingSettings, setMeetingSettings] = useState({
        guestCanUnmute: false,
    });
    const [showElementSection, setShowElementSection] = useState(false);
    const [folderElementList, setFolderElementList] = useState([]);
    const [activeElement, setActiveElement] = useState(null);
    const [webinarMode, setWebinarMode] = useState(null);
    const [meetingSession, setMeetingSession] = useState(null);
    const [selectedLayout, setSelectedLayout] = useState("gallery");
    const [hideSelfView, setHideSelfView] = useState(false);
    const [showNonVideoParticipants, setShowNonVideoParticipants] = useState(false);
    const [videoResolution, setVideoResolution] = useState('high');
    const [translationStatus, setTranslationStatus] = useState("stopped");
    const [localPinnedParticipant, setLocalPinnedParticipant] = useState([]);
    const [isMeetingReconnecting, setIsMeetingReconnecting] = useState(false);
    const [showPollResults, setShowPollResults] = useState(false);
    const [showPollsModal, setShowPollsModal] = useState(false);
    const [polls, setPolls] = useState([]);
    const [selectedPoll, setSelectedPoll] = useState(null);
    const [loadingPolls, setLoadingPolls] = useState(false);
    const [showPollResultsModal, setShowPollResultsModal] = useState(false);
    const [pollResults, setPollResults] = useState(null);
    const [customMeetingName, setCustomMeetingName] = useState("");
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [prompterElement, setPrompterElement] = useState(null);
    const [alreadyApprovedUsers, setAlreadyApprovedUsers] = useState([]);
    const [logoutFromMeeting, setLogoutFromMeeting] = useState(false);
    const [isAutoLogoutModalOpen, setIsAutoLogoutModalOpen] = useState(false);
    const [totalParticipants, setTotalParticipants] = useState(0);
    const [keepSessionOpenDuration, setKeepSessionOpenDuration] = useState(null);

    const keepSessionOpenDurationRef = useRef(keepSessionOpenDuration);
    const totalParticipantsRef = useRef(totalParticipants);

    useEffect(() => {
        keepSessionOpenDurationRef.current = keepSessionOpenDuration;
    }, [keepSessionOpenDuration]);

    useEffect(() => {
        totalParticipantsRef.current = totalParticipants;
    }, [totalParticipants]);

    // Note: Set default keep session open duration when modal opens
    useEffect(() => {
        if (isAutoLogoutModalOpen) {
            if (!keepSessionOpenDuration) {
                setKeepSessionOpenDuration(10);
            }
        }
    }, [isAutoLogoutModalOpen]);

    // Note: Opens modal after inactivity
    const logout = () => {
        // console.log('totalParticipants_out: ', totalParticipants);
        // console.log("keepSessionOpenDuration: ", keepSessionOpenDuration);

        const keepDuration = keepSessionOpenDurationRef.current;
        const participants = totalParticipantsRef.current;

        console.log("keepSessionOpenDuration:", keepDuration);
        console.log("totalParticipants:", participants);

        if (participants === 1) {
            if (keepDuration === null) {
                console.log('totalParticipants: ', participants);
                console.log("Logging out due to inactivity");
                setIsAutoLogoutModalOpen(true);
            }
        }
    };

    // Note: Forces logout if modal is ignored for 1 minute
    const forceLogout = () => {
        console.log("Forcing logout from meeting due to inactivity");
        setMeetingSession(null);
        setLogoutFromMeeting(true);
        setIsAutoLogoutModalOpen(false);
    };

    // Note: Initialize auto-logout hooks
    useAutoLogout(logout);
    // Note: Initialize force auto-logout hook
    useForceAutoLogout(isAutoLogoutModalOpen, forceLogout);

    // Load maintenance mode from localStorage
    useEffect(() => {
        const savedMaintenanceMode = localStorage.getItem('maintenanceMode');
        if (savedMaintenanceMode !== null) {
            setMaintenanceMode(savedMaintenanceMode === 'true');
        }

        if (localStorage.getItem(`meeting_already_approved_users_${meetingSession}`)) {
            const timestamp = localStorage.getItem(`meeting_already_approved_users_timestamp_${meetingSession}`);
            // check if timestamp is older than 3 hours
            if (timestamp && Date.now() - parseInt(timestamp) > 3 * 60 * 60 * 1000) {
                localStorage.removeItem(`meeting_already_approved_users_${meetingSession}`);
                localStorage.removeItem(`meeting_already_approved_users_timestamp_${meetingSession}`);
            } else {
                setAlreadyApprovedUsers(JSON.parse(localStorage.getItem(`meeting_already_approved_users_${meetingSession}`)));
            }
        }
    }, []);

    useEffect(() => {
        if (alreadyApprovedUsers && alreadyApprovedUsers.length > 0 && meetingSession) {
            localStorage.setItem(`meeting_already_approved_users_${meetingSession}`, JSON.stringify(alreadyApprovedUsers));
            localStorage.setItem(`meeting_already_approved_users_timestamp_${meetingSession}`, Date.now().toString());
        }
    }, [alreadyApprovedUsers]);

    // Save maintenance mode to localStorage when it changes
    const updateMaintenanceMode = (value) => {
        setMaintenanceMode(value);
        localStorage.setItem('maintenanceMode', value.toString());
    };

    useEffect(() => {
        const user = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        if (user && token) {
            setIsAuthenticated(true);
            setUser(JSON.parse(user));
            setToken(token);
        } else {
            setIsAuthenticated(false);
            setUser(null);
            setToken(null);
        }

        const ua = navigator.userAgent;
        const safariCheck = /^((?!chrome|android).)*safari/i.test(ua);
        setIsSafari(safariCheck);
    }, []);

    useEffect(() => {
        if (customMeetingName && meetingSettings && meetingSession) {
            meetingServices.updateCustomMeetingName({
                meetingId: meetingSettings.meetingId,
                sessionId: meetingSession,
                customName: customMeetingName,
            });
        }
    }, [customMeetingName, meetingSettings, meetingSession]);

    useEffect(() => {
        if (!(meetingSettings)) return;
        console.log("meetingSettings", meetingSettings);
        console.log("meetingRole", meetingRole);
        if (meetingSettings && meetingSettings.roomType == "Meeting") {
            setWebinarMode(Constants.modes.SEND_AND_RECV);
        } else if (meetingSettings && meetingSettings.roomType == "Webinar" && ["moderator", "co-host", "speaker"].includes(meetingRole)) {
            setWebinarMode(Constants.modes.SEND_AND_RECV);
        } else {
            setWebinarMode(Constants.modes.RECV_ONLY);
        }
    }, [meetingSettings, meetingRole]);

    const getMeetingSession = async (meetingId) => {
        try {
            const response = await meetingServices.getMeetingSession(meetingId);
            console.log("response from getMeetingSession", response, response.participants);
            setMeetingSession(response.data);
            // setMeetingSettings((prev) => ({
            //     ...prev,
            //     guestCanUnmute: response?.sessionSettings?.guestCanMute,
            //     hostApprovalRequired: response?.sessionSettings?.waitingRoomEnabled,
            // }));
        } catch (error) {
            console.log("Error in getMeetingSession", error);
        }
    }

    const handleVideoResolution = async (mMeeting, createCameraVideoTrack, resolution = 'high') => {
        console.log("handleVideoResolution", resolution, mMeeting, createCameraVideoTrack);
        const track = await createCameraVideoTrack({
            optimizationMode: "motion",
            encoderConfig: resolution === 'high' ? "h1080p_w1440p" : "h360p_w640p",
            facingMode: "environment",
            multiStream: false,
        });
        mMeeting.changeWebcam(track);
        setVideoResolution(resolution);
    }

    const handleAutoLogoutCancel = () => {
        setIsAutoLogoutModalOpen(false);
    }

    const handleAutoLogoutConfirm = () => {
        setIsAutoLogoutModalOpen(false);
        setMeetingSession(null);
        setLogoutFromMeeting(true);
    }

    const handleKeepSessionOpen = () => {
        console.log("Keeping session open");
        setIsAutoLogoutModalOpen(false);
        console.log("keepSessionOpenDuration: ", keepSessionOpenDuration);
        // Note: Convert minutes to milliseconds
        const durationInMs = parseInt(keepSessionOpenDuration) * 60 * 1000;
        console.log("durationInMs: ", durationInMs);
        setTimeout(() => {
            console.log("Auto logout triggered after keeping session open");
            // setIsAutoLogoutModalOpen(true);
            handleAutoLogoutConfirm();
        }, durationInMs);
    }

    const value = {
        isAuthenticated,
        setIsAuthenticated,
        user,
        setUser,
        token,
        setToken,
        meetingRole,
        setMeetingRole,
        isSafari,
        setMeetingSettings,
        meetingSettings,
        showElementSection,
        setShowElementSection,
        folderElementList,
        setFolderElementList,
        activeElement,
        setActiveElement,
        webinarMode,
        setWebinarMode,
        getMeetingSession,
        meetingSession,
        setMeetingSession,
        selectedLayout,
        setSelectedLayout,
        hideSelfView,
        setHideSelfView,
        showNonVideoParticipants,
        setShowNonVideoParticipants,
        videoResolution,
        setVideoResolution,
        handleVideoResolution,
        translationStatus,
        setTranslationStatus,
        localPinnedParticipant,
        setLocalPinnedParticipant,
        isMeetingReconnecting,
        setIsMeetingReconnecting,
        showPollResults,
        setShowPollResults,
        showPollsModal,
        setShowPollsModal,
        polls,
        setPolls,
        selectedPoll,
        setSelectedPoll,
        loadingPolls,
        setLoadingPolls,
        showPollResultsModal,
        setShowPollResultsModal,
        pollResults,
        setPollResults,
        customMeetingName,
        setCustomMeetingName,
        maintenanceMode,
        setMaintenanceMode: updateMaintenanceMode,
        prompterElement,
        setPrompterElement,
        alreadyApprovedUsers,
        setAlreadyApprovedUsers,
        isAutoLogoutModalOpen,
        handleAutoLogoutCancel,
        handleAutoLogoutConfirm,
        logoutFromMeeting,
        setTotalParticipants,
        handleKeepSessionOpen,
        setKeepSessionOpenDuration
    };

    return (
        <SiteContext.Provider value={value}>
            {children}
        </SiteContext.Provider>
    );
};

export default SiteContext;
